// The one per-vertex attribute of the engine: the corner index (R553).
//
// Every draw of the fast client is four vertices of a triangle strip. The
// corner used to come from `gl_VertexID`, so an instanced batch put a
// divisor-1 attribute at location 0 and a full-quad pass bound no vertex
// array at all. Firefox on a Mac emulates such a draw: it warns on every
// `drawArraysInstanced` and `drawArrays` that attribute 0 is not a
// per-vertex array, and it builds the missing array itself.
//
// So the engine ships ONE static buffer of four floats — 0, 1, 2, 3 — and
// binds it at location 0 with divisor 0 in every vertex array it makes.
// Each vertex shader reads `aCorner` and makes the same two corner bits
// from it, so nothing about the picture changes. The attribute is READ by
// every program, because an attribute the linker drops brings the emulation
// back.
//
// The buffer is one per `GL` wrapper. It is registered before any batch, so
// a context restore recreates it before a batch VAO binds it: `context.ts`
// recreates its resources in registration order.

import type { GL, GpuResource } from './context';
import type { AttribSpec } from '../gfx/types';

/** The location every per-vertex corner attribute takes. */
export const CORNER_LOCATION = 0;

/**
 * The corner as an attribute SPEC.
 *
 * Every program of the engine starts its attribute list with this entry: it
 * is the one per-vertex attribute, and the WGSL vertex-input header is
 * generated from the list. It sits beside the buffer it describes, so one
 * file states the location, the data and the shape.
 */
export const CORNER_ATTRIB: AttribSpec = {
  name: 'aCorner', location: CORNER_LOCATION, size: 1, offset: 0,
};

/** The four corners of a triangle strip, one index each. */
export const CORNER_DATA = new Float32Array([0, 1, 2, 3]);

/**
 * The two lines every vertex shader opens with: the corner index as an int,
 * then the unit corner `(ci & 1, ci >> 1)`. One chunk per language, so the
 * seven GLSL sources and their WGSL twins decode the strip one way.
 */
export const CORNER_DECODE_GLSL = `  int ci = int(aCorner + 0.5);
  vec2 corner = vec2(float(ci & 1), float(ci >> 1));`;

/** The WGSL twin of `CORNER_DECODE_GLSL`; `input` names the vertex-input value. */
export function cornerDecodeWgsl(input = 'in'): string {
  return `  let ci = i32(${input}.aCorner + 0.5);
  let corner = vec2f(f32(ci & 1), f32(ci >> 1u));`;
}

/** Uploads the four corner indices into a new static buffer. */
export function createCornerBuffer(gl: WebGL2RenderingContext): WebGLBuffer | null {
  const vbo = gl.createBuffer();
  if (!vbo) return null;
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, CORNER_DATA, gl.STATIC_DRAW);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return vbo;
}

/**
 * Binds the corner buffer as attribute 0 of the vertex array in use.
 *
 * The divisor is 0, because the attribute is per VERTEX. A vertex array can
 * outlive an instanced batch that left the divisor at 1 on the same
 * location, so the call is made every time, the way `mesh.ts` makes it.
 */
export function bindCorner(gl: WebGL2RenderingContext, cornerBuffer: WebGLBuffer | null): void {
  if (!cornerBuffer) return;
  gl.bindBuffer(gl.ARRAY_BUFFER, cornerBuffer);
  gl.enableVertexAttribArray(CORNER_LOCATION);
  gl.vertexAttribPointer(CORNER_LOCATION, 1, gl.FLOAT, false, 0, 0);
  gl.vertexAttribDivisor(CORNER_LOCATION, 0);
}

/** The static corner buffer of one context. `corners` hands it out. */
export class CornerBuffer implements GpuResource {
  private vbo: WebGLBuffer | null = null;

  /** The live handle. It is a NEW one after every context restore. */
  get buffer(): WebGLBuffer | null { return this.vbo; }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const vbo = createCornerBuffer(gl);
    if (!vbo) throw new Error('fast: cannot create the corner buffer');
    this.vbo = vbo;
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vbo) gl.deleteBuffer(this.vbo);
    this.vbo = null;
  }
}

/**
 * The vertex array of a full-quad pass: the corner buffer and nothing else.
 *
 * The three instanced batches make their own vertex array, because they
 * carry the instance attributes beside the corner. A pass that draws one
 * quad carries nothing else, so every one of them shares this array.
 */
export class CornerArray implements GpuResource {
  private vao: WebGLVertexArrayObject | null = null;
  private readonly corner: CornerBuffer;

  constructor(glc: GL) {
    // The buffer registers first, so the restore builds it first.
    this.corner = corners(glc);
    glc.register(this);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const vao = gl.createVertexArray();
    if (!vao) throw new Error('fast: cannot create the corner array');
    gl.bindVertexArray(vao);
    bindCorner(gl, this.corner.buffer);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.vao = vao;
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vao) gl.deleteVertexArray(this.vao);
    this.vao = null;
  }

  /** Binds the array for one draw. The caller unbinds it after. */
  bind(gl: WebGL2RenderingContext): void {
    gl.bindVertexArray(this.vao);
  }
}

const BUFFERS = new WeakMap<GL, CornerBuffer>();
const ARRAYS = new WeakMap<GL, CornerArray>();

/**
 * The one corner buffer of a context. The first caller registers it, so it
 * stands in front of every batch and every pass in the restore order.
 */
export function corners(glc: GL): CornerBuffer {
  const held = BUFFERS.get(glc);
  if (held) return held;
  const made = new CornerBuffer();
  BUFFERS.set(glc, made);
  glc.register(made);
  return made;
}

/** The one full-quad vertex array of a context. */
export function cornerArray(glc: GL): CornerArray {
  const held = ARRAYS.get(glc);
  if (held) return held;
  const made = new CornerArray(glc);
  ARRAYS.set(glc, made);
  return made;
}
